import { ArrowRight, Globe, Server, FileJson } from 'lucide-react';
import { useState } from 'react';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { normalizeTitle } from './title-model';

export function BrowserLayersLab() {
  const [styled, setStyled] = useState(false);
  const [behavior, setBehavior] = useState(false);
  const [title, setTitle] = useState('Connect the page');
  const [tasks, setTasks] = useState(['Learn HTTP', 'Build an API']);
  const [message, setMessage] = useState(
    'HTML supplies the content and native controls. Try submitting the form.',
  );
  return (
    <Lab
      title="Which layer changes which thing?"
      eyebrow="07 / Browser layers"
      note="Toggle styling and click behavior to see how CSS and JavaScript change the same HTML."
    >
      <div className="cb-layer-toggles">
        <span>
          HTML <strong>always on</strong>
        </span>
        <label>
          <input
            type="checkbox"
            checked={styled}
            onChange={(e) => {
              setStyled(e.target.checked);
              setMessage(
                'The presentation changed. The task data and DOM list still contain the same records.',
              );
            }}
          />
          CSS: presentation
        </label>
        <label>
          <input
            type="checkbox"
            checked={behavior}
            onChange={(e) => {
              setBehavior(e.target.checked);
              setMessage(
                e.target.checked
                  ? 'A submit handler can now update the list. Add a task, then switch CSS off.'
                  : 'The handler is off. Existing DOM content stays; future submissions no longer add tasks in this model.',
              );
            }}
          />
          JavaScript: behavior
        </label>
      </div>
      <div className="cb-browser-frame">
        <div className="cb-address">
          <Globe size={13} />
          127.0.0.1:8000
        </div>
        <div className="cb-rendered-page" data-styled={styled}>
          <h4>CollabBoard</h4>
          <p>What are you working on?</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!behavior) {
                setMessage(
                  'A native form would navigate to submit. No JavaScript handler is connected to add a task here.',
                );
                return;
              }
              if (!normalizeTitle(title)) {
                setMessage('The handler rejects a blank title. The list is unchanged.');
                return;
              }
              setTasks([...tasks, normalizeTitle(title)]);
              setTitle('');
              setMessage(
                'JavaScript handled the event and added a DOM row. CSS only changes how that row looks.',
              );
            }}
          >
            <label>
              Task title
              <input value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <button type="submit">Add task to page</button>
          </form>
          <ul>
            {tasks.map((task, index) => (
              <li key={index}>
                <span aria-hidden="true">○</span>
                {task}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="cb-layer-facts">
        <span>
          DOM list: <strong>{tasks.length} rows</strong>
        </span>
        <span>
          Server writes: <strong>0</strong>
        </span>
        <Reset
          onClick={() => {
            setStyled(false);
            setBehavior(false);
            setTitle('Connect the page');
            setTasks(['Learn HTTP', 'Build an API']);
            setMessage(
              'Back to HTML alone. The next experiment will connect this interface to a server.',
            );
          }}
        />
      </div>
      <Result>{message}</Result>
    </Lab>
  );
}

const outcomes = {
  success: { label: '201 · created', body: '{"id":2,"title":"Learn fetch","completed":false}' },
  reject: { label: '422 · rejected', body: '{"detail":"Title must not be blank"}' },
  refused: { label: 'Connection refused', body: 'No HTTP response exists' },
  lost: { label: 'Response lost after save', body: 'No response reaches the caller' },
};
type Outcome = keyof typeof outcomes;

export function BrowserNetworkLab() {
  const [outcome, setOutcome] = useState<Outcome>('success');
  const [phase, setPhase] = useState(0);
  const [title, setTitle] = useState('Learn fetch');
  const serverChanged = phase >= 2 && (outcome === 'success' || outcome === 'lost');
  const complete = phase === 4;
  const knownSuccess = complete && outcome === 'success';
  const pending = phase > 0 && !complete;
  const descriptions = [
    'The title is only in the form. No request has been sent.',
    'fetch returns a promise. The form shows pending state while the request travels.',
    outcome === 'refused'
      ? 'The connection was refused before delivery. The server did no work.'
      : outcome === 'reject'
        ? 'The server rejects invalid input. No task is added.'
        : 'The server has saved task #2. The browser has not received confirmation yet.',
    outcome === 'success'
      ? 'A 201 response arrives. The client can read its JSON body.'
      : outcome === 'reject'
        ? 'A 422 response arrives. fetch fulfills, but response.ok is false.'
        : 'The client receives a network error, not an HTTP status. It cannot read the server’s state.',
    outcome === 'success'
      ? 'Render the accepted task and clear the input. Only now does the page claim success.'
      : outcome === 'reject'
        ? 'Show the validation error and keep the title for correction.'
        : outcome === 'lost'
          ? 'Keep the input and report uncertainty. The server did save; an automatic new create could duplicate it.'
          : 'Keep the input and show a connection failure. No accepted task exists in this scenario.',
  ];
  return (
    <Lab
      title="The browser and server know different things"
      eyebrow="08 / An asynchronous request"
      note="Follow the request one step at a time. Each reset starts from one saved task."
    >
      <div className="cb-controls">
        <label>
          Server / network outcome
          <select
            disabled={phase !== 0}
            value={outcome}
            onChange={(e) => {
              const next = e.target.value as Outcome;
              setOutcome(next);
              setTitle(next === 'reject' ? '   ' : 'Learn fetch');
            }}
          >
            {Object.entries(outcomes).map(([value, item]) => (
              <option key={value} value={value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="cb-network-map">
        <span data-active={phase === 1}>
          <Globe size={17} />
          Browser
        </span>
        <ArrowRight size={17} aria-hidden="true" />
        <span data-active={phase === 2}>
          <Server size={17} />
          API
        </span>
        <ArrowRight size={17} aria-hidden="true" />
        <span data-active={serverChanged}>
          <FileJson size={17} />
          Saved state
        </span>
      </div>
      <div className="cb-two">
        <Panel label="What the person sees">
          <div className="cb-mini-form">
            <label>
              Title in the form
              <input
                aria-label="Title in the simulated form"
                value={knownSuccess ? '' : title}
                readOnly
              />
            </label>
            <button disabled={phase !== 0} onClick={() => setPhase(1)}>
              {pending ? 'Saving…' : 'Send create request'}
            </button>
          </div>
          <ul className="cb-task-stack">
            <li>
              <small>#1</small>Read HTTP
            </li>
            {knownSuccess && (
              <li>
                <small>#2</small>
                {title}
              </li>
            )}
          </ul>
          <div className="cb-ui-feedback" data-error={complete && !knownSuccess}>
            {phase === 0
              ? 'Ready'
              : pending
                ? 'Saving…'
                : knownSuccess
                  ? 'Saved'
                  : outcome === 'reject'
                    ? 'Enter a nonblank title.'
                    : 'Could not confirm the save. Your input is still here.'}
          </div>
        </Panel>
        <Panel label="What actually happened">
          <dl className="cb-memory">
            <div>
              <dt>Saved tasks</dt>
              <dd>{serverChanged ? 2 : 1}</dd>
            </div>
            <div>
              <dt>HTTP status seen by client</dt>
              <dd>
                {phase >= 3 && outcome === 'success'
                  ? '201'
                  : phase >= 3 && outcome === 'reject'
                    ? '422'
                    : 'none'}
              </dd>
            </div>
          </dl>
          <pre className="cb-response">
            {phase < 1
              ? 'POST /tasks has not been sent'
              : phase < 3
                ? `POST /tasks\n${JSON.stringify({ title })}\n\nAwaiting a response…`
                : outcomes[outcome].body}
          </pre>
        </Panel>
      </div>
      <div className="cb-request-steps" aria-label="Request progress">
        {['Draft', 'Request', 'Server', 'Response', 'Render'].map((label, index) => (
          <span key={label} data-current={phase === index} data-passed={phase > index}>
            {index + 1}. {label}
          </span>
        ))}
      </div>
      <div className="cb-controls">
        <button
          className="cb-action"
          disabled={phase === 0 || complete}
          onClick={() => setPhase(phase + 1)}
        >
          Deliver next step
        </button>
        <Reset
          label="Reset request"
          onClick={() => {
            setPhase(0);
            setTitle(outcome === 'reject' ? '   ' : 'Learn fetch');
          }}
        />
      </div>
      <Result tone={complete ? (knownSuccess ? 'success' : 'warning') : 'neutral'}>
        {descriptions[phase]}
      </Result>
    </Lab>
  );
}
